import { Check, Circle } from 'lucide-react';
import { commandAddresseeCode, humaniseTimes, withWholeDayTimes } from '@pams/shared';
import type { OrgUnitSummaryDto } from '@pams/shared';
import { AddresseeCodes } from '@/components/AddresseeCodes';
import { askedAddressees } from '@/components/intake/commandAsks';
import { StatusDot } from '@/components/StatusDot';
import { cn } from '@/lib/utils';
import { willCreate } from '@/lib/arrangementShape';
import { SURFACES } from '@/lib/surfaces';
import type { ArrangementPaDraft } from '../formValues';
import type { DutyPointView } from '../sections';
import type { ChecklistItem } from '../checklist';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE RIGHT COLUMN. THREE THINGS, IN THIS ORDER.
 *
 *  1. A section checklist with completion ticks. ⚠️ THIS IS THE PAGE'S NAVIGATION — the
 *     form is one long page rather than a wizard, so something has to answer "where am I
 *     and what is left", and a progress bar answers neither.
 *  2. The live ORDER PREVIEW, rendering as the printed order will.
 *  3. ⚠️ The "this will create" line, ALWAYS VISIBLE. Nobody should ever be surprised by
 *     what they just made.
 *
 *  ⚠️ THE PAGE CAPS THIS COLUMN'S HEIGHT, NOT `TwoColumnLayout`. A sticky element taller
 *  than the viewport is unreachable at its bottom — it does not scroll internally and
 *  `<main>` has already scrolled past it. `docs/design/DESIGN_LANGUAGE.md` §4 records the
 *  trap and says the consuming page fixes it; this is the page that meets it.
 *
 *  ⚠️ THE PERSONNEL FIGURE COMES FROM `willCreate`, WHICH HAS NO COMMAND PARAMETER.
 *  FR-5.2.6b is held by that signature rather than by an `if` here.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type RightRailProps = {
  draft: ArrangementPaDraft;
  dutyPoints: readonly DutyPointView[];
  deploymentCount: number;
  slotCount: number;
  divisions: readonly OrgUnitSummaryDto[];
  /**
   * ⚠️ [D4.4 §1b] EVERY ORG UNIT THE PAGE HAS RESOLVED, so the preview can name a source
   * rather than print an id. Until D4.4 the page passed a permanently empty `Map`, which is
   * why `divisions` above fell back to summaries whose `name` was a raw ObjectId.
   */
  orgUnits: ReadonlyMap<string, OrgUnitSummaryDto>;
  displayTitle: string;
  checklist: readonly ChecklistItem[];
  onJump: (id: string) => void;
};

export const RightRail = ({
  draft,
  dutyPoints,
  deploymentCount,
  slotCount,
  divisions,
  orgUnits,
  displayTitle,
  checklist,
  onJump,
}: RightRailProps) => (
  <div className="flex flex-col gap-4">
    {/* ── 1. The navigation. ─────────────────────────────────────────────────────── */}
    <nav aria-label="Sections" className="rounded-lg border border-border bg-card p-2" data-testid="section-checklist">
      <ul className="flex flex-col">
        {checklist.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onJump(item.id)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-hover"
            >
              {item.done ? (
                <Check className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              ) : (
                <Circle className="size-4 shrink-0 text-subtle" aria-hidden />
              )}
              <span className={cn('flex-1 truncate', !item.done && 'text-muted-foreground')}>{item.label}</span>
              {item.detail && <span className="shrink-0 text-xs text-subtle">{item.detail}</span>}
              <span className="sr-only">{item.done ? 'complete' : 'not started'}</span>
            </button>
          </li>
        ))}

        {/*
          ⚠️ [amended D4] THE CARDS NEST INSIDE THE ONE NAV, rather than becoming checklist
          rows of their own. `checklistOf` stays untouched and there is still one nav for a
          screen reader to walk; a card is a sub-row of `Duty points`, which is what it is.
        */}
        {dutyPoints.length > 0 && (
          <li>
            <ul className="ml-6 flex flex-col border-l border-border pl-2" data-testid="duty-point-nav">
              {dutyPoints.map((point) => (
                <li key={point.id}>
                  <button
                    type="button"
                    onClick={() => onJump(`dutyPoint-${point.id}`)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs hover:bg-hover"
                  >
                    <span className="flex-1 truncate text-muted-foreground">
                      {point.draft.locationNames[0] ?? `Duty point ${String(point.index + 1)}`}
                    </span>
                    <span className="shrink-0 tabular-nums text-subtle">{point.slots.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </li>
        )}
      </ul>
    </nav>

    {/* ── 2. What this will create. Always visible, above the fold of the preview. ── */}
    <section
      className="rounded-lg border border-border bg-secondary/40 px-3 py-2"
      data-testid="will-create"
      aria-live="polite"
    >
      <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">This will create</h3>
      <p className="mt-1 text-sm tabular-nums">
        {willCreate({
          deploymentCount,
          dutyPointCount: dutyPoints.length,
          placeCount: dutyPoints.reduce((total, point) => total + point.draft.placeCount, 0),
          /*
           * ⚠️ EVERY CARD'S TOTALS, CONCATENATED. `willCreate` only SUMS them, and it has no
           * parameter through which a command role could arrive — FR-5.2.6b held by the
           * signature rather than by an `if`, exactly as before.
           */
          requirementTotals: dutyPoints.flatMap((point) => point.grid.totals),
          divisionCount: divisions.length,
        })}
      </p>
      <p className="mt-0.5 text-xs text-subtle tabular-nums">
        {String(slotCount)} distinct shift{slotCount === 1 ? '' : 's'}
      </p>
    </section>

    {/* ── 3. The order preview. [D4 §6] HIDDEN WITH A CLASS, NEVER UNMOUNTED. ─────── */}
    <section
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card',
        !SURFACES.intakeOrderPreview && 'hidden',
      )}
      data-testid="order-preview"
    >
      <header className="border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold tracking-tight">Order preview</h3>
      </header>

      <div className="flex flex-col gap-3 p-3 text-xs">
        <div>
          <p className="text-sm font-medium">{displayTitle}</p>
          {draft.paDate && <p className="text-muted-foreground">Order dated {draft.paDate}</p>}
        </div>

        {dutyPoints.map((point) => (
          <div key={point.id} className="border-t border-dashed border-border pt-2 first:border-0 first:pt-0">
            <p className="font-medium">
              {point.draft.locationNames.join(' → ') || `Duty point ${String(point.index + 1)}`}
            </p>
            <p className="text-subtle">{point.policyName}</p>

            {point.slots.length > 0 && (
              <ul className="mt-0.5 flex flex-col text-muted-foreground">
                {/*
                  ⚠️ [C3a §6] THE RAIL READS A **STORED** LABEL TOO. `generateSlots`' whole-day
                  branch stamps `<date> · No fixed time`, and this preview sits on the same
                  screen as the duty point card whose own phrase §6 converted — so left raw the
                  two disagreed about one card, a scroll apart.
                  ⚠️ `withReportingTime` AFTER `humaniseTimes`, never before: the noon meridiem
                  doubles otherwise, which `timeFormat.util.ts` measured.
                */}
                {point.slots.slice(0, 4).map((slot) => (
                  <li key={slot.slotKey}>
                    {withWholeDayTimes(humaniseTimes(slot.label), point.draft.reportingTime, point.draft.standDownTime)}
                  </li>
                ))}
                {point.slots.length > 4 && <li className="text-subtle">and {point.slots.length - 4} more</li>}
              </ul>
            )}

            {point.grid.columns.length > 0 && (
              <ul className="mt-1 flex flex-col text-muted-foreground">
                {point.grid.columns.map((column) => (
                  <li key={column.lineIndex} className="flex justify-between gap-2">
                    <span className="truncate">{column.label}</span>
                    <span className="tabular-nums">{point.grid.totals[column.lineIndex] ?? 0}</span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              ⚠️ [D4.4 §1b] THE PRINTED ORDER SAYS WHO IS BEING ASKED. §1b covers the list,
              the detail and the printed order, and this is the third — the sheet an operator
              hands over is the one place where "20 UB Men" with no addressee is useless,
              because the reader cannot go and click anything to find out.

              ⚠️ IT IS BUILT EVEN THOUGH `SURFACES.intakeOrderPreview` IS `false` TODAY. The
              hidden branch is live code by design — `surfaces.test.tsx` renders both sides —
              so leaving the division out here would ship a preview that is already wrong
              the day somebody flips the flag.
            */}
            {point.grid.sources.length > 0 && (
              <p className="mt-1 flex flex-wrap items-center gap-1 text-muted-foreground">
                <span className="text-subtle">Asked of</span>
                {point.grid.sources.map((source) => (
                  <AddresseeCodes
                    key={source.key}
                    division={orgUnits.get(source.divisionOrgUnitId) ?? null}
                    unit={source.unitOrgUnitId ? (orgUnits.get(source.unitOrgUnitId) ?? null) : null}
                  />
                ))}
              </p>
            )}

            {/*
              ⚠️ COMMAND RENDERS OUTSIDE THE FORCE BLOCK, as a sibling and never a row in it
              — the same rule `RequirementPreview` follows, for the same reason. FR-5.2.6b
              closes OD-9 precisely because everybody assumes command counts toward force,
              and the two places that show force together are where saying otherwise costs
              least.
            */}
            <div className="mt-1 border-t border-dashed border-border pt-1">
              <p className="font-medium">Command</p>
              <p className="text-subtle">Not counted in the force above.</p>
              <ul className="mt-0.5 flex flex-col text-muted-foreground">
                {point.roles.map((role) => {
                  const entry = point.draft.commandEntries.find((row) => row.roleKey === role.key);
                  /*
                   * ⚠️ [C6a §2] AN ASKED ROLE SAYS WHO IT IS ASKED OF, IN WORDS — never "—", which
                   * reads as "nobody will do this", and never a number: how many positions a
                   * division is asked for is not a figure this preview puts beside the force.
                   */
                  const askedOf = askedAddressees(entry?.asks).map((row) =>
                    commandAddresseeCode(
                      orgUnits.get(row.divisionOrgUnitId) ?? { shortCode: '—' },
                      row.unitOrgUnitId === null ? null : (orgUnits.get(row.unitOrgUnitId) ?? { shortCode: '—' }),
                    ),
                  );
                  return (
                    <li key={role.key} className="flex justify-between gap-2">
                      <span className="truncate">{role.label}</span>
                      <span>
                        {entry && entry.officerIds.length > 0 ? (
                          <StatusDot tone="success" label="named" />
                        ) : askedOf[0] !== undefined ? (
                          <span className="text-subtle" data-testid={`rail-command-asked-${role.key}`}>
                            asked of {askedOf.join(', ')}
                          </span>
                        ) : (
                          <span className="text-subtle">—</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);
